import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, MapPin, Bed, Bath, Maximize2, Phone, MessageCircle, User2, Check, Building2, CalendarCheck, ShieldCheck, Clock } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import PropertyCard from "../components/PropertyCard";
import MapView from "../components/MapView";
import { DocStatus } from "../components/DocStatus";
import { api } from "../lib/api";
import { useLang } from "../context/LangContext";
import { useRate } from "../context/RateContext";
import { formatDA, formatEUR, translations } from "../lib/i18n";
import { localized } from "../lib/api";

export default function PropertyDetail() {
  const { id } = useParams();
  const { t, lang } = useLang();
  const { rate } = useRate();
  const [p, setP] = useState(null);
  const [active, setActive] = useState(0);

  useEffect(() => { api.get(`/properties/${id}`).then((r) => setP(r.data)).catch(() => {}); }, [id]);

  if (!p) return <div><Header /><div className="pt-32 text-center text-slate-500">{t("loading")}</div></div>;

  const typeLabel = { apartment: t("apartment"), house: t("house"), land: t("land") }[p.type] || p.type;
  const title = localized(p, "title", lang);
  const description = localized(p, "description", lang);
  const waNum = (p.whatsapp || p.phone || "").replace(/[^0-9]/g, "");
  const priceStr = p.price_on_request ? "" : formatDA(p.price_da);
  const wa = translations[lang].waMessage(p.reference, p.city, priceStr) + ` ${window.location.href}`;
  const waLink = `https://wa.me/${waNum}?text=${encodeURIComponent(wa)}`;
  const waVisit = translations[lang].waVisitMsg ? translations[lang].waVisitMsg(p.reference, p.city) + ` ${window.location.href}` : wa;
  const waVisitLink = `https://wa.me/${waNum}?text=${encodeURIComponent(waVisit)}`;

  // Posted date
  let postedLabel = "";
  if (p.created_at) {
    const days = Math.floor((Date.now() - new Date(p.created_at).getTime()) / (1000 * 60 * 60 * 24));
    postedLabel = days === 0 ? t("justPosted") : translations[lang].daysAgo(days);
  }

  return (
    <div>
      <Header />
      <div className="pt-20 max-w-7xl mx-auto px-5 md:px-10">
        <Link to="/search" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-[#1B8F5A] mt-6" data-testid="back-link"><ArrowLeft size={16} />{t("back")}</Link>

        <div className="mt-5 grid grid-cols-1 md:grid-cols-4 gap-3 md:gap-4">
          <div className="md:col-span-3 aspect-[16/10] rounded-2xl overflow-hidden bg-slate-100">
            <img src={p.images[active]} alt={title} className="w-full h-full object-cover" />
          </div>
          <div className="md:col-span-1 grid grid-cols-4 md:grid-cols-1 gap-3">
            {p.images.slice(0, 4).map((img, i) => (
              <button key={i} onClick={() => setActive(i)} className={`aspect-[4/3] rounded-xl overflow-hidden border-2 ${active === i ? "border-[#1B8F5A]" : "border-transparent opacity-80 hover:opacity-100"}`} data-testid={`thumb-${i}`}>
                <img src={img} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-bold uppercase tracking-wider text-[#1B8F5A] bg-[#1B8F5A]/10 px-2.5 py-1 rounded">{p.transaction === "sale" ? t("forSale") : t("forRent")}</span>
              <span className="text-xs font-bold uppercase tracking-wider text-slate-700 bg-slate-100 px-2.5 py-1 rounded">{typeLabel}</span>
              {p.featured && <span className="text-xs font-bold uppercase tracking-wider text-white bg-[#F59E0B] px-2.5 py-1 rounded inline-flex items-center gap-1"><ShieldCheck size={11} />{t("verified")}</span>}
              {p.condition === "new" && <span className="text-xs font-bold uppercase text-white bg-[#1B8F5A] px-2.5 py-1 rounded">{t("new")}</span>}
              {p.payment_type === "installment" && <span className="text-xs font-bold uppercase text-amber-800 bg-amber-100 px-2.5 py-1 rounded">{t("installment")}</span>}
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500 ml-auto" data-testid="ref-code">{p.reference}</span>
            </div>

            <h1 className="text-3xl md:text-4xl font-bold text-[#111827] mt-4 leading-tight">{title}</h1>
            <div className="flex items-center gap-2 text-slate-600 mt-3"><MapPin size={16} /><span>{p.neighborhood ? `${p.neighborhood}, ` : ""}{p.city}, {p.wilaya}</span></div>
            {postedLabel && <div className="flex items-center gap-2 text-slate-500 mt-1 text-sm"><Clock size={13} /><span>{t("postedOn")} {postedLabel}</span></div>}

            <div className="mt-8">
              {p.price_on_request ? (
                <div className="text-3xl md:text-4xl font-bold text-[#F59E0B]" data-testid="detail-price">{t("priceOnRequest")}</div>
              ) : (
                <>
                  <div className="text-4xl md:text-5xl font-bold text-[#F59E0B]" data-testid="detail-price-da">{formatDA(p.price_da)}{p.transaction === "rent" && <span className="text-lg text-slate-500 font-medium">{t("perMonth")}</span>}</div>
                  <div className="text-slate-500 mt-1" data-testid="detail-price-eur">
                    {formatEUR(p.price_da, rate)}
                    <span className="block text-xs text-slate-400 mt-0.5">{t("eurNote")}</span>
                  </div>
                </>
              )}
            </div>

            <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3">
              <Info icon={Maximize2} label={t("surface")} value={`${p.surface} m²`} />
              {p.rooms > 0 && <Info icon={Bed} label={t("rooms")} value={p.rooms} />}
              {p.bathrooms > 0 && <Info icon={Bath} label={t("bathrooms")} value={p.bathrooms} />}
              <Info icon={Building2} label={t("type")} value={typeLabel} />
            </div>

            <div className="mt-10">
              <h2 className="text-2xl font-bold text-[#111827]">{t("features")}</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-5">
                {[["furnished", p.furnished], ["balcony", p.balcony], ["parking", p.parking], ["swimmingPool", p.swimming_pool], ["garden", p.garden], ["airConditioning", p.air_conditioning], ["heating", p.heating], ["elevator", p.elevator]].map(([k, v]) =>
                  v ? <Feature key={k} label={t(k)} /> : null
                )}
              </div>
              {(p.floor !== null && p.floor !== undefined) || p.orientation ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                  {p.floor !== null && p.floor !== undefined && <Info icon={Building2} label={t("floor")} value={p.floor === 0 ? "RDC" : p.floor} />}
                  {p.orientation && <Info icon={Building2} label={t("orientation")} value={p.orientation} />}
                </div>
              ) : null}
            </div>

            <div className="mt-10" data-testid="documents-section">
              <h2 className="text-2xl font-bold text-[#111827]">{t("documents")}</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-5">
                <div data-testid="doc-status-title-deed"><DocStatus value={p.title_deed} label={t("titleDeed")} /></div>
                <div data-testid="doc-status-land-registry"><DocStatus value={p.land_registry} label={t("landRegistry")} /></div>
                <div data-testid="doc-status-negative-cert"><DocStatus value={p.negative_cert} label={t("negativeCert")} /></div>
              </div>
            </div>

            <div className="mt-10">
              <h2 className="text-2xl font-bold text-[#111827]">{t("aboutProperty")}</h2>
              <p className="text-slate-700 mt-4 leading-relaxed whitespace-pre-line">{description}</p>
            </div>

            {p.lat && (
              <div className="mt-10">
                <h2 className="text-2xl font-bold text-[#111827] mb-4">{t("location_")}</h2>
                <MapView items={[p]} height={360} />
              </div>
            )}
          </div>

          <aside className="lg:col-span-1">
            <div className="sticky top-24 bg-white rounded-2xl border border-slate-200 p-7 shadow-[0_20px_40px_-15px_rgba(27,143,90,0.12)]">
              <div className="flex items-center gap-3 pb-5 border-b border-slate-100">
                <div className="w-11 h-11 rounded-full bg-[#1B8F5A]/10 flex items-center justify-center"><User2 size={18} className="text-[#1B8F5A]" /></div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs uppercase tracking-wider text-slate-500">{t("postedBy")}</div>
                  {p.owner_id && p.owner_id !== "seed" ? (
                    <Link to={`/profile/${p.owner_id}`} className="font-semibold text-[#111827] hover:text-[#1B8F5A] block truncate" data-testid="owner-link">{p.owner_name}</Link>
                  ) : (
                    <div className="font-semibold text-[#111827] truncate">{p.owner_name}</div>
                  )}
                  <div className="text-xs text-slate-500">{p.owner_role === "agency" ? t("agency") : t("privateOwner")}</div>
                </div>
              </div>
              <h3 className="text-base font-bold mt-5 text-[#111827]">{t("contactOwner")}</h3>
              <a href={waLink} target="_blank" rel="noreferrer" className="mt-4 w-full bg-[#25D366] hover:bg-[#1EBE57] text-white flex items-center justify-center gap-2 py-3.5 rounded-xl font-semibold transition" data-testid="whatsapp-btn"><MessageCircle size={18} />{t("messageWhatsapp")}</a>
              <a href={waVisitLink} target="_blank" rel="noreferrer" className="mt-3 w-full bg-[#1B8F5A] hover:bg-[#157047] text-white flex items-center justify-center gap-2 py-3.5 rounded-xl font-semibold transition" data-testid="visit-btn"><CalendarCheck size={17} />{t("requestVisit")}</a>
              <a href={`tel:${p.phone}`} className="mt-3 w-full bg-slate-50 hover:bg-slate-100 border border-slate-200 text-[#111827] flex items-center justify-center gap-2 py-3.5 rounded-xl font-semibold transition" data-testid="call-btn"><Phone size={17} />{t("callNow")}</a>
            </div>
          </aside>
        </div>

        {p.similar?.length > 0 && (
          <section className="mt-20">
            <h2 className="text-2xl md:text-3xl font-bold text-[#111827] mb-6">{t("similarProperties")}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">{p.similar.map((s) => <PropertyCard key={s.id} item={s} />)}</div>
          </section>
        )}
      </div>

      {/* Mobile sticky CTA */}
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-slate-200 p-3 flex gap-2 md:hidden" data-testid="mobile-sticky-cta">
        <a href={waLink} target="_blank" rel="noreferrer" className="flex-1 bg-[#25D366] text-white flex items-center justify-center gap-2 py-3 rounded-xl font-semibold"><MessageCircle size={17} />WhatsApp</a>
        <a href={`tel:${p.phone}`} className="flex-1 bg-[#1B8F5A] text-white flex items-center justify-center gap-2 py-3 rounded-xl font-semibold"><Phone size={17} />{t("callNow")}</a>
      </div>

      <Footer />
    </div>
  );
}

function Info({ icon: Icon, label, value }) {
  return (
    <div className="bg-slate-50 rounded-xl p-4">
      <div className="flex items-center gap-2 text-[#1B8F5A]"><Icon size={15} /><span className="text-[10px] uppercase tracking-wider font-bold">{label}</span></div>
      <div className="text-lg font-bold text-[#111827] mt-1">{value}</div>
    </div>
  );
}

function Feature({ label }) {
  return <div className="flex items-center gap-2 text-sm text-slate-700"><Check size={16} className="text-[#1B8F5A]" />{label}</div>;
}
